import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageRecordDialogComponent, LanguageRecordDialogData } from '@shared/localization';
import { filter, take } from 'rxjs/operators';

/**
 * Dumb chips input for localized strings (`Record<lang, value>` entries). It
 * holds no state: the parent owns the `chips` array and reacts to the
 * `chipAdded` / `chipRemoved` / `chipUpdated` outputs.
 *
 * Typing free text and confirming with Enter or comma proposes a new chip as
 * `{ en: text }`; per-language values are edited through the shared
 * `LanguageRecordDialogComponent`, which this component opens itself (the
 * dumb-opens-own-dialog pattern blessed by `MethodAssetFieldsManagerComponent`).
 */
@Component({
  selector: 'ts-localized-chips-input',
  templateUrl: './localized-chips-input.component.html',
  styleUrls: ['./localized-chips-input.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, MatDialogModule],
})
export class LocalizedChipsInputComponent {
  @Input() chips: Record<string, string>[] = [];
  @Input() currentLanguage = 'en';

  /** i18n key for the free-text input placeholder. */
  @Input() placeholderKey = '';

  /** i18n key for the translation dialog title. */
  @Input() dialogTitleKey = 'method_type_editor.dialog.sample_name_translations';

  @Output() chipAdded = new EventEmitter<Record<string, string>>();
  @Output() chipRemoved = new EventEmitter<number>();
  @Output() chipUpdated = new EventEmitter<{ index: number; values: Record<string, string> }>();

  constructor(private readonly dialog: MatDialog) {}

  /** Chip label: current UI language, falling back to `en`, then to any non-empty value. */
  public chipLabel(chip: Record<string, string>): string {
    return chip[this.currentLanguage] || chip['en'] || Object.values(chip).find((value) => !!value) || '';
  }

  public onInputKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ',') return;
    // Swallow the confirming keystroke so the comma never lands in the input.
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const text = input.value.trim();
    if (!text) return;
    this.chipAdded.emit({ en: text });
    input.value = '';
  }

  public onTranslateChip(index: number): void {
    const chip = this.chips[index];
    if (!chip) return;
    const data: LanguageRecordDialogData = {
      values: chip,
      requiredLanguages: ['en'],
      titleKey: this.dialogTitleKey,
    };
    this.dialog
      .open<LanguageRecordDialogComponent, LanguageRecordDialogData, Record<string, string> | undefined>(
        LanguageRecordDialogComponent,
        { width: '400px', hasBackdrop: true, data }
      )
      .afterClosed()
      .pipe(
        take(1),
        filter((values): values is Record<string, string> => !!values)
      )
      .subscribe((values) => this.chipUpdated.emit({ index, values }));
  }

  public onRemoveChip(index: number): void {
    this.chipRemoved.emit(index);
  }
}
